import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { InstitutionsAPI, CampusesAPI, TenantsAPI } from "@/lib/sprint2-api";
import { PortalSlugEditor } from "@/components/PortalSlugEditor";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, GraduationCap, MapPin, Loader2, Trash2, Pencil, Image as ImageIcon } from "lucide-react";
import BrandingDialog from "@/components/branding/BrandingDialog";

const TYPES = [
  { value: "k12", label: "K-12 school" },
  { value: "college", label: "College" },
  { value: "university", label: "University" },
  { value: "training", label: "Training centre" },
];

export default function Institutions() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const queryClient = useQueryClient();

  // For super_admin we need to pick a tenant context; for others it's their tenant.
  const [tenantId, setTenantId] = useState("");

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });

  // Auto-pick first tenant for super_admin (derived — keeps lint happy)
  const effectiveTenantId = isSuper
    ? (tenantId || tenantsQuery.data?.[0]?.id || "")
    : (user?.tenant_id || "");
  const tenantParam = isSuper && effectiveTenantId ? { tenant_id: effectiveTenantId } : undefined;

  const institutionsQuery = useQuery({
    queryKey: ["institutions", effectiveTenantId],
    queryFn: () => InstitutionsAPI.list(tenantParam),
    enabled: !!effectiveTenantId,
  });

  const campusesQuery = useQuery({
    queryKey: ["campuses", effectiveTenantId],
    queryFn: () => CampusesAPI.list(tenantParam),
    enabled: !!effectiveTenantId,
  });

  const [selectedInst, setSelectedInst] = useState(null);
  const [editingInst, setEditingInst] = useState(null);
  const [brandingInst, setBrandingInst] = useState(null);

  return (
    <AppShell>
      <div className="p-8" data-testid="institutions-page">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
<h1 className="font-display font-extrabold tracking-tight text-4xl">Institutions &amp; Campuses</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              An institution is a legal educational entity inside a tenant.
              Each institution can host multiple physical campuses.
            </p>
          </div>
          <div className="flex items-end gap-3">
            {isSuper && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Tenant</Label>
                <Select value={effectiveTenantId} onValueChange={(v) => { setTenantId(v); setSelectedInst(null); }}>
                  <SelectTrigger className="w-[240px]" data-testid="tenant-selector">
                    <SelectValue placeholder="Select a tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenantsQuery.data?.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <CreateInstitution
              tenantId={effectiveTenantId}
              isSuper={isSuper}
              disabled={!effectiveTenantId}
              onDone={() => queryClient.invalidateQueries({ queryKey: ["institutions", effectiveTenantId] })}
            />
          </div>
        </div>

        {!effectiveTenantId && (
          <div className="border border-dashed border-border rounded-md p-12 text-center bg-card">
            <p className="text-sm text-muted-foreground">
              {isSuper ? "Select a tenant from the dropdown above to start." : "No tenant attached to your account."}
            </p>
          </div>
        )}

        {effectiveTenantId && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Institutions list */}
            <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <GraduationCap className="h-4 w-4 text-primary" />
                <div className="font-display font-bold">Institutions</div>
                <Badge variant="outline" className="ml-auto text-[10px]">
                  {institutionsQuery.data?.length || 0}
                </Badge>
              </div>
              {institutionsQuery.isLoading ? (
                <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
              ) : institutionsQuery.data?.length ? (
                <ul className="divide-y divide-border" data-testid="institutions-list">
                  {institutionsQuery.data.map((i) => (
                    <li
                      key={i.id}
                      className={`px-5 py-3 cursor-pointer hover:bg-secondary/50 transition ${
                        selectedInst === i.id ? "bg-secondary/70 border-l-2 border-primary" : ""
                      }`}
                      onClick={() => setSelectedInst(i.id)}
                      data-testid={`institution-row-${i.id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{i.name}</div>
                          <div className="text-xs text-muted-foreground font-mono flex items-center gap-2 flex-wrap">
                            <span>{i.type} {i.board ? `· ${i.board}` : ""}</span>
                            <PortalSlugEditor
                              kind="institution"
                              id={i.id}
                              value={i.slug}
                              onSaved={() => queryClient.invalidateQueries({ queryKey: ["institutions"] })}
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="text-[10px] uppercase">{i.type}</Badge>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); setEditingInst(i); }}
                            title="Edit institution"
                            data-testid={`institution-edit-${i.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); setBrandingInst(i); }}
                            title="Branding · logo & background"
                            data-testid={`institution-brand-${i.id}`}
                          >
                            <ImageIcon className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Delete "${i.name}"? Its campuses, academic years, classes, sections and subjects will also be removed.`)) return;
                              try {
                                await InstitutionsAPI.remove(i.id);
                                if (selectedInst === i.id) setSelectedInst(null);
                                queryClient.invalidateQueries({ queryKey: ["institutions"] });
                                queryClient.invalidateQueries({ queryKey: ["campuses"] });
                                toast.success("Institution removed");
                              } catch (err) {
                                toast.error(formatApiErrorDetail(err));
                              }
                            }}
                            title="Delete institution"
                            data-testid={`institution-delete-${i.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="p-10 text-center">
                  <GraduationCap className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm font-medium">No institutions yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Create your first institution above.</p>
                </div>
              )}
            </div>

            {/* Campuses for selected institution */}
            <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <div className="font-display font-bold">
                  Campuses {selectedInst ? "" : "—  select an institution"}
                </div>
                {selectedInst && (
                  <CreateCampus
                    institutionId={selectedInst}
                    onDone={() => queryClient.invalidateQueries({ queryKey: ["campuses", effectiveTenantId] })}
                  />
                )}
              </div>
              {!selectedInst ? (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  Click an institution on the left to see its campuses.
                </div>
              ) : (
                <CampusList
                  campuses={(campusesQuery.data || []).filter((c) => c.institution_id === selectedInst)}
                  loading={campusesQuery.isLoading}
                  onSaved={() => queryClient.invalidateQueries({ queryKey: ["campuses", effectiveTenantId] })}
                  onDelete={async (id) => {
                    const campus = (campusesQuery.data || []).find((c) => c.id === id);
                    if (!window.confirm(`Delete campus "${campus?.name || id}"?`)) return;
                    try {
                      await CampusesAPI.remove(id);
                      toast.success("Campus removed");
                      queryClient.invalidateQueries({ queryKey: ["campuses", effectiveTenantId] });
                    } catch (e) {
                      console.error("Campus delete failed:", e);
                      toast.error(formatApiErrorDetail(e));
                    }
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
      {editingInst && (
        <EditInstitutionDialog
          institution={editingInst}
          onClose={() => setEditingInst(null)}
          onSaved={() => {
            setEditingInst(null);
            queryClient.invalidateQueries({ queryKey: ["institutions", effectiveTenantId] });
          }}
        />
      )}
      {brandingInst && (
        <BrandingDialog
          kind="institutions"
          id={brandingInst.id}
          name={brandingInst.name}
          current={{ logo_url: brandingInst.logo_url, background_url: brandingInst.background_url }}
          onClose={() => setBrandingInst(null)}
        />
      )}
    </AppShell>
  );
}

function EditInstitutionDialog({ institution, onClose, onSaved }) {
  const [name, setName] = useState(institution.name || "");
  const [type, setType] = useState(institution.type || "k12");
  const [board, setBoard] = useState(institution.board || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await InstitutionsAPI.update(institution.id, {
        name: name.trim(),
        type,
        board: board.trim() || null,
      });
      toast.success("Institution updated");
      onSaved?.();
    } catch (e) {
      setError(formatApiErrorDetail(e) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`edit-institution-dialog-${institution.id}`}>
        <DialogHeader><DialogTitle>Edit institution</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="edit-institution-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="edit-institution-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="k12">K-12</SelectItem>
                  <SelectItem value="higher_ed">Higher Education</SelectItem>
                  <SelectItem value="coaching">Coaching</SelectItem>
                  <SelectItem value="mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Board (optional)</Label>
              <Input value={board} onChange={(e) => setBoard(e.target.value)} placeholder="CBSE / ICSE / IB …" data-testid="edit-institution-board" />
            </div>
          </div>
          {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="edit-institution-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CampusList({ campuses, loading, onDelete, onSaved }) {
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();
  if (loading) {
    return <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></div>;
  }
  if (!campuses.length) {
    return (
      <div className="p-10 text-center">
        <MapPin className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-sm font-medium">No campuses yet</p>
        <p className="text-xs text-muted-foreground mt-1">Add the first campus for this institution.</p>
      </div>
    );
  }
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Campus</TableHead>
            <TableHead>City</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {campuses.map((c) => (
            <TableRow
              key={c.id}
              data-testid={`campus-row-${c.id}`}
              className="cursor-pointer hover:bg-muted/40"
              onClick={() => navigate(`/campuses?id=${c.id}`)}
            >
              <TableCell>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.address || "—"}</div>
              </TableCell>
              <TableCell className="text-sm">
                {c.city ? `${c.city}, ${c.state || ""}` : "—"}
              </TableCell>
              <TableCell className="text-right">
                <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditing(c); }} title="Edit" data-testid={`campus-edit-${c.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); onDelete(c.id); }} data-testid={`campus-delete-${c.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {editing && (
        <EditCampusDialog
          campus={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onSaved?.(); }}
        />
      )}
    </>
  );
}

function EditCampusDialog({ campus, onClose, onSaved }) {
  const [name, setName] = useState(campus.name || "");
  const [address, setAddress] = useState(campus.address || "");
  const [city, setCity] = useState(campus.city || "");
  const [state, setState] = useState(campus.state || "");
  const [country, setCountry] = useState(campus.country || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await CampusesAPI.update(campus.id, {
        name: name.trim(),
        address: address.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        country: country.trim() || null,
      });
      toast.success("Campus updated");
      onSaved?.();
    } catch (e) {
      setError(formatApiErrorDetail(e) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`edit-campus-dialog-${campus.id}`}>
        <DialogHeader><DialogTitle>Edit campus</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Campus name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="edit-campus-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="edit-campus-address" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="edit-campus-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">State</Label>
              <Input value={state} onChange={(e) => setState(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Country</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value)} />
            </div>
          </div>
          {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="edit-campus-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateInstitution({ tenantId, isSuper, disabled, onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("k12");
  const [board, setBoard] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [country, setCountry] = useState("IN");
  const [pincode, setPincode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await InstitutionsAPI.create({
        name, type,
        board: board || undefined,
        email: email || undefined,
        phone: phone || undefined,
        website: website || undefined,
        address: address || undefined,
        city: city || undefined,
        state: stateVal || undefined,
        country: country || undefined,
        pincode: pincode || undefined,
        tenant_id: isSuper ? tenantId : undefined,
      });
      toast.success("Institution created");
      setName(""); setBoard(""); setType("k12");
      setEmail(""); setPhone(""); setWebsite("");
      setAddress(""); setCity(""); setStateVal(""); setPincode("");
      setOpen(false);
      onDone?.();
    } catch (e) {
      console.error("Institution save failed:", e);
      setError(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled} data-testid="create-institution-button">
          <Plus className="h-4 w-4 mr-1.5" /> Institution
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New institution</DialogTitle>
          <DialogDescription>Adds an institution under the current tenant.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="inst-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="inst-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Board / Affiliation</Label>
              <Input value={board} onChange={(e) => setBoard(e.target.value)} placeholder="CBSE, UGC, IB…" data-testid="inst-form-board" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="office@school.com" data-testid="inst-form-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 99998 88882" data-testid="inst-form-phone" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Website</Label>
            <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.com" data-testid="inst-form-website" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="inst-form-address" />
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="inst-form-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">State</Label>
              <Input value={stateVal} onChange={(e) => setStateVal(e.target.value)} data-testid="inst-form-state" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Country</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value)} data-testid="inst-form-country" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Pincode</Label>
              <Input value={pincode} onChange={(e) => setPincode(e.target.value)} data-testid="inst-form-pincode" />
            </div>
          </div>
          {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="inst-form-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateCampus({ institutionId, onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("IN");
  const [pincode, setPincode] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [principalName, setPrincipalName] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [geofenceRadius, setGeofenceRadius] = useState("200");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      // Build geo object only when at least a lat/lng pair is present so
      // partial data doesn't create a half-empty geofence record.
      const geo = {};
      if (lat && lng) {
        const latN = parseFloat(lat), lngN = parseFloat(lng);
        if (Number.isNaN(latN) || Number.isNaN(lngN)) {
          setError("Latitude and longitude must be numbers");
          setLoading(false);
          return;
        }
        geo.lat = latN;
        geo.lng = lngN;
        const rad = parseInt(geofenceRadius, 10);
        if (!Number.isNaN(rad) && rad > 0) geo.geofence_radius_m = rad;
      }
      await CampusesAPI.create({
        institution_id: institutionId,
        name, address, city, state, country,
        pincode: pincode || undefined,
        phone: phone || undefined,
        email: email || undefined,
        principal_name: principalName || undefined,
        geo,
      });
      toast.success("Campus added");
      setName(""); setAddress(""); setCity(""); setState("");
      setPincode(""); setPhone(""); setEmail(""); setPrincipalName("");
      setLat(""); setLng(""); setGeofenceRadius("200");
      setOpen(false);
      onDone?.();
    } catch (e) {
      console.error("Campus save failed:", e);
      setError(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="ml-auto" data-testid="create-campus-button">
          <Plus className="h-3.5 w-3.5 mr-1" /> Campus
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New campus</DialogTitle>
          <DialogDescription>Full campus profile — used for parent contact, receipts and staff geofence.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Campus name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Main Campus" data-testid="campus-form-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="campus-form-address" />
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="campus-form-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">State</Label>
              <Input value={state} onChange={(e) => setState(e.target.value)} data-testid="campus-form-state" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Country</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value)} data-testid="campus-form-country" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Pincode</Label>
              <Input value={pincode} onChange={(e) => setPincode(e.target.value)} data-testid="campus-form-pincode" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9919101150" data-testid="campus-form-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="campus@school.com" data-testid="campus-form-email" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Principal name</Label>
            <Input value={principalName} onChange={(e) => setPrincipalName(e.target.value)} data-testid="campus-form-principal" />
          </div>
          <div className="pt-2 border-t border-slate-200">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              Location & Geofence (optional — enables staff check-in)
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-wider">Latitude</Label>
                <Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="27.019984" data-testid="campus-form-lat" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-wider">Longitude</Label>
                <Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="83.375243" data-testid="campus-form-lng" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-wider">Geofence radius (m)</Label>
                <Input value={geofenceRadius} onChange={(e) => setGeofenceRadius(e.target.value)} placeholder="200" data-testid="campus-form-geofence" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Long-press the school on Google Maps to copy exact coordinates. 200m default works for most campuses.
            </p>
          </div>
          {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="campus-form-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
